import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

interface CompareSliderProps {
  beforeSrc: string;
  afterSrc: string;
  beforeAlt: string;
  afterAlt: string;
}

export default function CompareSlider({ beforeSrc, afterSrc, beforeAlt, afterAlt }: CompareSliderProps) {
  const [position, setPosition] = useState(50);
  const draggingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.min(100, Math.max(0, pct)));
  }, []);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    containerRef.current?.setPointerCapture(event.pointerId);
    updateFromClientX(event.clientX);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    updateFromClientX(event.clientX);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    containerRef.current?.releasePointerCapture(event.pointerId);
  };

  return (
    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      className="relative aspect-[4/3] w-full cursor-ew-resize touch-none select-none overflow-hidden rounded-md bg-foreground-950"
    >
      <img
        src={afterSrc}
        alt={afterAlt}
        title={afterAlt}
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-top"
      />
      <img
        src={beforeSrc}
        alt={beforeAlt}
        title={beforeAlt}
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-top"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      />

      <span className="pointer-events-none absolute left-3 top-3 rounded-sm bg-foreground-950/70 px-2.5 py-1 font-label text-[11px] font-black tracking-wider text-background-50 backdrop-blur-sm">
        BEFORE
      </span>
      <span className="pointer-events-none absolute right-3 top-3 rounded-sm bg-primary-500 px-2.5 py-1 font-label text-[11px] font-black tracking-wider text-background-50">
        AFTER
      </span>

      <div
        className="pointer-events-none absolute bottom-0 top-0 w-0.5 bg-background-50"
        style={{ left: `${position}%` }}
      >
        <span className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-background-50 bg-foreground-950/70 text-background-50 backdrop-blur-sm">
          <i className="ri-drag-move-2-line text-lg"></i>
        </span>
      </div>
    </div>
  );
}